// Tiny helper for inserting Cloudinary transformation parameters into an
// upload URL so we don't fetch a 2000×2000 source for a 40px avatar.
//
// Cloudinary URL anatomy:
//   https://res.cloudinary.com/<cloud>/image/upload/<transforms>/<public-id>
//
// We splice transforms in right after `/image/upload/`. Calls are no-ops
// for:
//   - non-string / falsy input
//   - non-Cloudinary URLs (we get base64 data URLs from local file
//     pickers and the placeholder URL passes through unchanged)
//   - URLs that already contain a transformation segment (presence of
//     well-known transform tokens) — we don't want to double-stack
//     transforms and accidentally degrade quality

const UPLOAD_MARKER = "/image/upload/";

// Heuristic for "this segment already contains a transform string". The
// segment right after /image/upload/ is either a version (`v1234567`),
// the public id, or a comma-joined transform list. Transform lists
// typically start with one of these prefixes.
const TRANSFORM_PREFIXES = [
  "c_", "w_", "h_", "f_", "q_", "g_", "r_", "x_", "y_",
  "ar_", "dpr_", "e_", "fl_", "o_", "l_", "u_", "t_",
];

const looksLikeTransformSegment = (segment) =>
  TRANSFORM_PREFIXES.some((p) => segment.startsWith(p));

// `opts` follows Cloudinary's transformation shorthand:
//   { w: 120, h: 120, c: 'fill', f: 'auto', q: 'auto', g: 'face' }
// Sensible defaults applied for missing values when a transform should
// actually be inserted (i.e. caller passed at least one explicit param).
export const transformCloudinary = (url, opts = {}) => {
  if (!url || typeof url !== "string") return url;
  if (!url.includes(UPLOAD_MARKER)) return url; // not a Cloudinary upload URL

  const [prefix, rest] = url.split(UPLOAD_MARKER);
  const firstSegment = rest.split("/")[0] || "";
  if (looksLikeTransformSegment(firstSegment)) {
    // Already has a transform segment — leave it alone.
    return url;
  }

  const params = {
    f: "auto", // pick the best format the browser supports (webp/avif/jpg)
    q: "auto", // pick the smallest quality that still looks good
    ...opts,
  };
  // Tailor the c / g defaults for the common avatar case where w & h
  // are both set: square crop centered on face when available.
  if (params.w && params.h && !params.c) params.c = "fill";
  if (params.c === "fill" && !params.g) params.g = "auto";

  const transform = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}_${v}`)
    .join(",");

  if (!transform) return url;
  return `${prefix}${UPLOAD_MARKER}${transform}/${rest}`;
};

// Convenience preset for circular avatars rendered at small sizes.
// Multiplies the CSS pixel size by 2 internally so retina screens get a
// sharp image without us having to think about DPR at the call site.
export const avatarUrl = (url, sizePx = 40) =>
  transformCloudinary(url, {
    w: Math.round(sizePx * 2),
    h: Math.round(sizePx * 2),
    c: "fill",
    g: "auto",
    f: "auto",
    q: "auto",
  });
